import { createContext, useContext, useId } from 'react';
import { RiErrorWarningLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';

/* ------------------------------------------------------------------------------------------------
 * Field context — connects the label / hint / error of a <Field> to the control inside it.
 * Input, PasswordInput, SearchInput, Textarea, Select, MultiSelect and Slider read it.
 * --------------------------------------------------------------------------------------------- */

export interface FieldContextValue {
  controlId: string;
  labelId: string;
  describedBy: string | undefined;
  invalid: boolean;
  disabled: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

/** Props a control should spread to be labelled/described by the surrounding <Field>. */
export function useFieldControlProps(props: {
  id?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
}) {
  const field = useFieldContext();
  const describedBy = [props['aria-describedby'], field?.describedBy].filter(Boolean).join(' ') || undefined;
  return {
    id: props.id ?? field?.controlId,
    disabled: props.disabled ?? field?.disabled,
    required: props.required ?? field?.required,
    invalid: props.invalid ?? field?.invalid ?? false,
    'aria-describedby': describedBy,
    labelId: field?.labelId,
  };
}

/* ------------------------------------------------------------------------------------------------ */

/** Caps caption of glass popovers: 600 11/auto, +0.5 px, uppercase, White/60 (382:6614). */
export const CAPS_POPOVER = 'text-11 leading-auto font-semibold tracking-caps text-fg-secondary uppercase';

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Adds the red `*` (600 11 #ca3b3d). */
  required?: boolean;
  /**
   * `panel` — 600 11/15 caps, White/60 (side sheets, modals, 501:11603).
   * `popover` — 600 11/auto caps +0.5 px tracking (glass popovers 382:6614).
   * `kit` — 600 14/20 white, not uppercased (kit Input & Select 67:10970, modal B 429:16562).
   */
  variant?: 'panel' | 'popover' | 'kit';
}

const LABEL_VARIANT = {
  panel: 't-caps',
  popover: CAPS_POPOVER,
  kit: 'text-sm font-semibold text-fg',
} as const;

/** Product field label — `t-caps` (uppercase, White/60). Text is uppercased by CSS so translations stay consistent. */
export function Label({ required, variant = 'panel', className, children, ...rest }: LabelProps) {
  return (
    <label
      {...rest}
      className={cn('block', LABEL_VARIANT[variant], className)}
    >
      {children}
      {required && (
        <span aria-hidden className="ml-2 text-tone-red">
          *
        </span>
      )}
    </label>
  );
}

/** Caps caption that is not a form label: "XODIM: AZIZ KARIMOV", "MAXSUS RANG (HEX)", section captions. */
export function FieldCaption({
  as: Tag = 'p',
  variant = 'panel',
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLElement> & { as?: 'p' | 'span' | 'div' | 'h4'; variant?: 'panel' | 'popover' }) {
  return (
    <Tag {...rest} className={cn(variant === 'popover' ? CAPS_POPOVER : 't-caps', className)}>
      {children}
    </Tag>
  );
}

/** Helper text under a control: 400 11/15 White/60; error variant in tone red with a warning glyph. */
export function FieldMessage({
  id,
  tone = 'hint',
  className,
  children,
}: {
  id?: string;
  tone?: 'hint' | 'error';
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <p
      id={id}
      role={tone === 'error' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-4 text-11',
        tone === 'error' ? 'text-tone-red-lighter' : 'text-fg-secondary',
        className,
      )}
    >
      {tone === 'error' && <RiErrorWarningLine aria-hidden className="mt-1 size-13 shrink-0" />}
      <span className="min-w-0">{children}</span>
    </p>
  );
}

export interface FieldProps {
  /** Caps label above the control (gap 8). */
  label?: React.ReactNode;
  /** Right side of the label row — counter "0 / 1000", "8 ta modul", link buttons… */
  labelAside?: React.ReactNode;
  labelVariant?: LabelProps['variant'];
  required?: boolean;
  /** Helper text below the control (400 11/15 White/60, gap 8). */
  hint?: React.ReactNode;
  /** Error message — replaces the hint and marks the control invalid. */
  error?: React.ReactNode;
  disabled?: boolean;
  /** Explicit id for the control (otherwise generated). */
  id?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Field wrapper (spec §4.5 product field): caps label, control, hint/error. Gives the control an id and
 * wires `aria-describedby` / `aria-invalid` through context.
 */
export function Field({ label, labelAside, labelVariant, required = false, hint, error, disabled = false, id, className, children }: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `f${autoId}`;
  const labelId = `${controlId}-label`;
  const messageId = `${controlId}-message`;
  const invalid = error != null && error !== false && error !== '';
  const message = invalid ? error : hint;
  return (
    <FieldContext
      value={{
        controlId,
        labelId,
        describedBy: message ? messageId : undefined,
        invalid,
        disabled,
        required,
      }}
    >
      <div
        className={cn('flex min-w-0 flex-col', labelVariant === 'kit' ? 'gap-6' : 'gap-8', disabled && 'opacity-60', className)}
        data-invalid={invalid || undefined}
      >
        {(label || labelAside) && (
          <div className="flex min-h-15 items-center justify-between gap-8">
            {label ? (
              <Label id={labelId} htmlFor={controlId} required={required} variant={labelVariant}>
                {label}
              </Label>
            ) : (
              <span />
            )}
            {labelAside && <div className="shrink-0 text-11 text-fg-secondary">{labelAside}</div>}
          </div>
        )}
        {children}
        {message && (
          <FieldMessage id={messageId} tone={invalid ? 'error' : 'hint'}>
            {message}
          </FieldMessage>
        )}
      </div>
    </FieldContext>
  );
}
